import * as yup from 'yup';
import { ItemCategory, ItemCommissionType } from '@prisma/client';
import {
  ProductDetail,
  ProductPayload,
  VocabularyAttribute,
} from '../../types/catalog';
import { ItemWithCommissions } from '../../types/items';
import { ImageItem } from '../form/appointmentForm';
import { TYPE_ATTRIBUTE_KEY, orderedAttributes } from './catalogFilters';

export interface VariantRowValues {
  id?: string;
  /** Stable React key for a row not saved yet; never sent to the API */
  clientKey?: string;
  /** Label set outside the form (imports); sent back so a save keeps it */
  overrideLabel?: string | null;
  /** attribute key → value label, for the attributes that vary */
  values: Record<string, string>;
  price: number | '';
  cost: number | '';
  sku: string;
  /** The variant's own image; null shows the product's elsewhere */
  image: ImageItem | null;
}

export interface ProductFormValues {
  name: string;
  category: ItemCategory | '';
  typeLabel: string;
  brandName: string;
  description: string;
  isTaxable: boolean;
  commissionType: ItemCommissionType;
  employeeCommissions: { employeeId: string; rate: number }[];
  images: ImageItem[];
  /** attribute key → value label shared by every variant */
  attributeValues: Record<string, string>;
  varyingKeys: string[];
  /** varying attribute key → the values the shop carries; rows are their combinations */
  axisValues: Record<string, string[]>;
  variants: VariantRowValues[];
}

// keep in sync with MAX_PRODUCT_VARIANTS in api/src/constants/products.ts
export const MAX_PRODUCT_VARIANTS = 2048;

const money = yup
  .mixed<number | ''>()
  .test(
    'money',
    'Must be 0 or more',
    value =>
      value === '' ||
      value === undefined ||
      (typeof value === 'number' && value >= 0),
  );

export const productFormSchema = yup.object({
  name: yup.string().trim().required('Name is required'),
  category: yup
    .mixed<ItemCategory>()
    .oneOf(Object.values(ItemCategory))
    .required('Category is required'),
  variants: yup
    .array()
    .of(
      yup.object({
        price: yup
          .number()
          .typeError('Every variant needs a price')
          .min(0, 'Price cannot be less than 0.00')
          .required('Every variant needs a price'),
        cost: money,
      }),
    )
    .min(1, 'Add at least one variant'),
});

export const emptyVariantRow = (): VariantRowValues => ({
  clientKey: crypto.randomUUID(),
  values: {},
  price: '',
  cost: '',
  sku: '',
  image: null,
});

export const EMPTY_PRODUCT_FORM: ProductFormValues = {
  name: '',
  category: '',
  typeLabel: '',
  brandName: '',
  description: '',
  isTaxable: true,
  commissionType: ItemCommissionType.REVENUE,
  employeeCommissions: [],
  images: [],
  attributeValues: {},
  varyingKeys: [],
  axisValues: {},
  variants: [emptyVariantRow()],
};

/** Attributes whose form fields show for this category, type excluded. */
export function attributesForCategory(
  attributes: VocabularyAttribute[],
  category: ItemCategory | '',
) {
  if (!category) return [];
  return orderedAttributes(attributes).filter(
    a => a.key !== TYPE_ATTRIBUTE_KEY && a.categories.includes(category),
  );
}

/** Porter and shop values applicable to the category, in sort order. */
export function valueLabelsFor(
  attribute: VocabularyAttribute | undefined,
  category: ItemCategory | '',
) {
  if (!attribute) return [];
  return attribute.values
    .filter(
      v =>
        !v.categories.length || (category && v.categories.includes(category)),
    )
    .map(v => v.label);
}

export function typeLabelsFor(
  attributes: VocabularyAttribute[],
  category: ItemCategory | '',
) {
  return valueLabelsFor(
    attributes.find(a => a.key === TYPE_ATTRIBUTE_KEY),
    category,
  );
}

/** The distinct values present per varying attribute, in first-seen order. */
export function axisValuesFromRows(
  rows: VariantRowValues[],
  varyingKeys: string[],
): Record<string, string[]> {
  return Object.fromEntries(
    varyingKeys.map(key => [
      key,
      [
        ...new Set(
          rows.map(r => r.values[key]).filter((v): v is string => !!v),
        ),
      ],
    ]),
  );
}

const rowKey = (values: Record<string, string>, keys: string[]) =>
  keys.map(key => values[key] ?? '').join('\u0000');

/** Rows the varying values would generate; an attribute with no values yet counts as one. */
export const combinationCount = (
  varyingKeys: string[],
  axisValues: Record<string, string[]>,
) => varyingKeys.reduce((n, key) => n * (axisValues[key]?.length || 1), 1);

/** The row without values for attributes that no longer vary. */
function withoutAttributes(row: VariantRowValues, keys: string[]) {
  if (!keys.some(key => key in row.values)) return row;
  return {
    ...row,
    values: Object.fromEntries(
      Object.entries(row.values).filter(([key]) => !keys.includes(key)),
    ),
  };
}

/** The row with each attribute it lacks set to that attribute's first value. */
function withFirstValues(
  row: VariantRowValues,
  keys: string[],
  axisValues: Record<string, string[]>,
) {
  const missing = keys.filter(key => !row.values[key]);
  if (!missing.length) return row;
  return {
    ...row,
    values: {
      ...row.values,
      ...Object.fromEntries(missing.map(key => [key, axisValues[key][0]])),
    },
  };
}

/** The row sharing the most values with `values`; earlier rows win ties. */
function closestRow(
  values: Record<string, string>,
  rows: VariantRowValues[],
  keys: string[],
) {
  let best: VariantRowValues | undefined;
  let bestScore = -1;
  rows.forEach(row => {
    const score = keys.filter(key => row.values[key] === values[key]).length;
    if (score > bestScore) {
      best = row;
      bestScore = score;
    }
  });
  return best;
}

const pricingFrom = (row: VariantRowValues | undefined) =>
  row ? { price: row.price, cost: row.cost } : {};

/** Writes the same price or cost into every row. */
export function applyToAllVariants(
  rows: VariantRowValues[],
  patch: Partial<Pick<VariantRowValues, 'price' | 'cost'>>,
): VariantRowValues[] {
  return rows.map(row => ({ ...row, ...patch }));
}

/**
 * Every combination of the selected values, keeping rows that already exist
 * (their id, price, cost, sku) and dropping rows whose values were unchecked.
 * An attribute with no values selected counts as not varying.
 */
export function generateVariantRows(
  rows: VariantRowValues[],
  varyingKeys: string[],
  axisValues: Record<string, string[]>,
): VariantRowValues[] {
  const activeKeys = varyingKeys.filter(key => axisValues[key]?.length);
  const clearedKeys = varyingKeys.filter(key => !axisValues[key]?.length);
  // every chip removed: the rows drop that value and merge like Varies was turned off
  const current = rows.map(row => withoutAttributes(row, clearedKeys));

  // nothing varies, so every row would describe the same item
  if (!activeKeys.length) return current.slice(0, 1);

  const combinations = activeKeys.reduce<Record<string, string>[]>(
    (acc, key) =>
      acc.flatMap(partial =>
        axisValues[key].map(value => ({ ...partial, [key]: value })),
      ),
    [{}],
  );
  // a row saved before an attribute varied takes its first value, so it keeps its id, price and SKU
  const existing = new Map(
    current
      .map(row => withFirstValues(row, activeKeys, axisValues))
      .map(row => [rowKey(row.values, activeKeys), row]),
  );
  return combinations.map(
    values =>
      existing.get(rowKey(values, activeKeys)) ?? {
        ...emptyVariantRow(),
        ...pricingFrom(closestRow(values, [...existing.values()], activeKeys)),
        values,
      },
  );
}

/** Sets one price on every row carrying `value` for `key`. */
export function setPriceForValue(
  rows: VariantRowValues[],
  key: string,
  value: string,
  price: number | '',
): VariantRowValues[] {
  return rows.map(row => (row.values[key] === value ? { ...row, price } : row));
}

const toImages = (urls: string[]): ImageItem[] =>
  urls.map((url, i) => ({ id: `existing-${i}`, url, isUploaded: true }));

/** Splits a product's facts into shared values and per-variant columns. */
export function formValuesFromProduct(
  product: ProductDetail,
): ProductFormValues {
  const facts = product.variants.map(v =>
    Object.fromEntries(
      Object.entries(v.attributes).filter(
        ([key]) => key !== TYPE_ATTRIBUTE_KEY,
      ),
    ),
  );
  const keys = [...new Set(facts.flatMap(f => Object.keys(f)))];
  const distinct = (key: string) =>
    new Set(facts.map(f => f[key]).filter((v): v is string => !!v));

  const varyingKeys = keys.filter(key => distinct(key).size > 1);
  const attributeValues = Object.fromEntries(
    keys
      .filter(key => distinct(key).size === 1)
      .map(key => [key, [...distinct(key)][0]]),
  );
  const variants: VariantRowValues[] = product.variants.map((variant, i) => ({
    id: variant.id,
    overrideLabel: variant.overrideLabel,
    values: Object.fromEntries(
      varyingKeys
        .map(key => [key, facts[i][key]] as const)
        .filter((entry): entry is readonly [string, string] => !!entry[1]),
    ),
    price: variant.price,
    cost: variant.cost ?? '',
    sku: variant.sku ?? '',
    image: variant.imageUrl
      ? { id: `variant-${variant.id}`, url: variant.imageUrl, isUploaded: true }
      : null,
  }));

  return {
    name: product.name,
    category: product.category,
    typeLabel: product.typeLabel ?? '',
    brandName: product.brand?.name ?? '',
    description: product.description ?? '',
    isTaxable: product.variants[0]?.isTaxable ?? true,
    commissionType: product.commissionType ?? ItemCommissionType.REVENUE,
    employeeCommissions: product.employeeCommissions,
    images: toImages(product.imageUrls),
    attributeValues,
    varyingKeys,
    axisValues: axisValuesFromRows(variants, varyingKeys),
    variants,
  };
}

/** A standalone item becomes a one-variant product on save. */
export function formValuesFromItem(
  item: ItemWithCommissions,
): ProductFormValues {
  return {
    ...EMPTY_PRODUCT_FORM,
    name: item.name,
    category: item.category,
    isTaxable: item.isTaxable,
    commissionType: item.commissionType ?? ItemCommissionType.REVENUE,
    employeeCommissions: item.employeeCommissions.map(c => ({
      employeeId: c.employeeId,
      rate: c.rate,
    })),
    images: toImages(item.images.map(i => i.url)),
    variants: [
      {
        id: item.id,
        values: {},
        price: item.price,
        cost: item.cost ?? '',
        sku: item.sku ?? '',
        image: null,
      },
    ],
  };
}

/** Preview of the label the API will generate, in attribute order. */
export function rowLabel(
  row: VariantRowValues,
  varyingKeys: string[],
  attributes: VocabularyAttribute[],
) {
  const order = orderedAttributes(attributes).map(a => a.key);
  return [...varyingKeys]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map(key => row.values[key])
    .filter(Boolean)
    .join(' · ');
}

const clean = (values: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(values)
      .map(([k, v]) => [k, v.trim()])
      .filter(([, v]) => !!v),
  );

/** `variantImageUrls` lines up with `values.variants`, already uploaded. */
export function buildProductPayload(
  values: ProductFormValues,
  imageUrls: string[],
  variantImageUrls: (string | null)[],
): ProductPayload {
  const shared = clean(values.attributeValues);
  return {
    name: values.name.trim(),
    category: values.category as ItemCategory,
    description: values.description.trim() || null,
    brandName: values.brandName.trim() || null,
    typeLabel: values.typeLabel.trim() || null,
    imageUrls,
    commissionType: values.commissionType,
    employeeCommissions: values.employeeCommissions,
    variants: values.variants.map((row, index) => ({
      id: row.id,
      imageUrl: variantImageUrls[index] ?? null,
      ...(row.overrideLabel ? { variantLabel: row.overrideLabel } : {}),
      // validated as a number before submit
      price: Number(row.price),
      cost: typeof row.cost === 'number' ? row.cost : null,
      isTaxable: values.isTaxable,
      sku: row.sku.trim() || null,
      attributes: {
        ...shared,
        ...clean(
          Object.fromEntries(
            values.varyingKeys.map(key => [key, row.values[key] ?? '']),
          ),
        ),
      },
    })),
  };
}

/** Rates are stored as fractions; a blank or zero rate drops the employee. */
export function withCommissionRate(
  commissions: ProductFormValues['employeeCommissions'],
  employeeId: string,
  percent: number | undefined,
): ProductFormValues['employeeCommissions'] {
  const others = commissions.filter(c => c.employeeId !== employeeId);
  return percent ? [...others, { employeeId, rate: percent / 100 }] : others;
}
